import type { RoomsParticipantDistribution } from '@/domain/shared/types'
import { space, tok } from '@sunwebgroup/ui-components'
import { Tag, Typography } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { styled } from 'styled-components'
import { useIntl } from '@/intl'
import { PriceDetails } from '@/scopes/common/components/PriceDetails'
import { AcmInformation, PriceDiscounts } from '@/scopes/search/search/infrastructure/api/types'

interface PriceProps {
  averagePrice: number
  discounts?: PriceDiscounts
  accommodationId: number
  participants?: RoomsParticipantDistribution
  className?: string
  acmInformation?: AcmInformation
}

const PriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`
const PriceHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${space('primitives_space_8')};
  align-self: stretch;

  ${Typography} {
    color: ${tok('semantics_content_danger_color')};
    text-decoration: line-through;
  }

  ${Tag} {
    background-color: ${tok('primitives_color_primary_150')};

    ${Typography} {
      color: ${tok('primitives_color_primary_700')};
      text-decoration: none;
    }
  }
`
const PriceSupportingText = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${space('primitives_space_2')};
`

function PriceComponent({ className, averagePrice, discounts, accommodationId, participants, acmInformation }: PriceProps) {
  const intl = useIntl()
  const priceText = intl.formatMessage({ id: 'price_box_price_text' }, { isPerPerson: true })
  const formattedPrice = React.useMemo(() => intl.formatTruncatedCurrency(averagePrice), [averagePrice, intl])
  const formattedPriceDiscounts = React.useMemo(() => {
    if (!discounts || discounts.discountPercentage <= 0)
      return undefined

    const absoluteDiscountValue = discounts.originalAveragePrice - averagePrice

    return {
      discountLabel: `-${intl.formatCurrency(absoluteDiscountValue)}`,
      originalPrice: intl.formatCurrency(discounts.originalAveragePrice),
    }
  }, [discounts, intl, averagePrice])

  const hasDiscount = React.useMemo(() => (
    formattedPriceDiscounts != null && !!formattedPriceDiscounts.discountLabel
  ), [formattedPriceDiscounts])

  return (
    <PriceWrapper className={className}>
      <PriceHeader>
        {hasDiscount && formattedPriceDiscounts && (
          <Typography variant="body-s-regular-tight">{formattedPriceDiscounts.originalPrice}</Typography>
        )}
        {hasDiscount && formattedPriceDiscounts && (
          <Tag>{formattedPriceDiscounts.discountLabel}</Tag>
        )}
      </PriceHeader>
      <Typography variant="display-b-regular" data-qa-price-value={formattedPrice}>
        {formattedPrice}
      </Typography>
      <PriceSupportingText>
        <Typography>
          {priceText}
        </Typography>
        {acmInformation && (
          <PriceDetails
            priceDetailsQueryParams={{
              transportType: acmInformation.transportType,
              accommodationId,
              duration: acmInformation.durationRaw,
              mealPlan: acmInformation.mealplanCode,
              departureDate: acmInformation.departureDateRaw,
              participants,
            }}
          />
        )}
      </PriceSupportingText>
      {discounts?.description && (
        <PriceSupportingText>
          <Typography variant="body-s-regular-tight" data-qa-price-discount-description>
            {discounts.description}
          </Typography>
        </PriceSupportingText>
      )}
    </PriceWrapper>
  )
}

export const Price = styled(PriceComponent)``
